<template>
  <div class="pablo-shell">
    <div class="panel">
      <h1 class="page-heading">Change Password</h1>
      <p class="subtitle">Set a new password to continue.</p>

      <form @submit.prevent="submit" class="form">
        <label v-if="!mustChange" class="field">
          <span class="label-text">Current password</span>
          <input
            v-model="old_password"
            class="input"
            type="password"
            autocomplete="current-password"
          />
        </label>

        <label class="field">
          <span class="label-text">New password</span>
          <input
            v-model="new_password"
            class="input"
            type="password"
            autocomplete="new-password"
            required
          />
          <ul class="rules">
            <li>At least <b>{{ minLen }}</b> characters</li>
            <li>No spaces</li>
            <li>Include at least 3 of: lowercase, uppercase, number, symbol</li>
          </ul>
        </label>

        <label class="field">
          <span class="label-text">Confirm new password</span>
          <input
            v-model="confirm"
            class="input"
            type="password"
            autocomplete="new-password"
            required
          />
        </label>

        <button class="btn" :disabled="loading" type="submit">
          {{ loading ? 'Updating…' : 'Update password' }}
        </button>

        <p v-if="error" class="error">{{ error }}</p>
        <p v-if="success" class="success">✅ Password updated. Redirecting…</p>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ChangePasswordPage',
  data() {
    return {
      old_password: '',
      new_password: '',
      confirm: '',
      loading: false,
      error: '',
      success: false,
      mustChange: false,
      minLen: 12, // keep UI in sync with backend env default
    };
  },
  async created() {
    // Prefer backend truth over route query
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const me = await axios.get('/auth/me', { headers });
      this.mustChange = !!me?.data?.must_change_pwd;
    } catch (e) {
      // fallback to query flag if /auth/me isn't available for some reason
      this.mustChange = this.$route.query.mustChange === '1';
    }
  },
  methods: {
    async submit() {
      this.error = '';
      this.success = false;

      if (this.new_password !== this.confirm) {
        this.error = 'Passwords do not match.';
        return;
      }

      // Client-side guidance only (server is source of truth)
      if (this.new_password.length < this.minLen) {
        this.error = `Password must be at least ${this.minLen} characters.`;
        return;
      }
      if (/\s/.test(this.new_password)) {
        this.error = 'Password may not contain spaces.';
        return;
      }

      this.loading = true;
      try {
        const token = localStorage.getItem('access_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const body = this.mustChange
          ? { new_password: this.new_password }
          : { old_password: this.old_password, new_password: this.new_password };

        await axios.post('/auth/change-password', body, { headers });

        this.success = true;
        setTimeout(() => this.$router.replace('/dashboard'), 800);
      } catch (e) {
        // show server error verbatim if provided
        this.error = e?.response?.data?.error || e?.response?.data?.message || e.message || 'Request failed.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Page shell: dark Pablo vibe */
.pablo-shell {
  min-height: 100vh;
  width: 100%;
  background-color: #0b0101;
  color: #cbbfbf;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Garamond, serif;
  position: relative;
  overflow: hidden;
  padding: 28px 14px;
}

/* Panel uses your machine-row slate/blue-gray */
.panel {
  width: 100%;
  max-width: 520px;
  background-color: #5595b7c2;
  border: 1px solid rgba(203, 191, 191, 0.28);
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
  padding: 22px 20px 20px 20px;
}

/* Heading style matches your dashboard */
.page-heading {
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 48px;
  color: #cbbfbf;
  margin: 0 0 6px 0;
  letter-spacing: 2px;
  text-align: center;
}

.subtitle {
  font-size: 20px;
  opacity: 0.85;
  margin: 0 0 18px 0;
  text-align: center;
}

/* Form */
.form {
  display: grid;
  gap: 14px;
}

/* Field */
.field {
  display: grid;
  gap: 8px;
}

.label-text {
  font-weight: 700;
  color: #f9fafb;
  letter-spacing: 0.3px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.35);
}

.input {
  padding: 10px 12px;
  border: 1px solid rgba(203, 191, 191, 0.38);
  border-radius: 10px;
  font-size: 14px;
  color: #0b0101;
  background-color: #7affe276; /* unscheduled card wash */
  outline: none;
}

.input:focus {
  border-color: rgba(203, 191, 191, 0.7);
  box-shadow: 0 0 0 3px rgba(122, 255, 226, 0.16);
}

/* Requirements text */
.rules {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: rgba(249, 250, 251, 0.92);
  line-height: 1.25rem;
}

/* Button feels like Pablo option hover */
.btn {
  margin-top: 6px;
  padding: 11px 14px;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 10px;
  background-color: #7affe276; /* unscheduled card */
  color: #111;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.18);
  transition: transform 0.12s ease, background-color 0.12s ease, box-shadow 0.12s ease;
}

.btn:hover {
  transform: translateY(-2px);
  background-color: #5595b7c2;
  color: #0b0101;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.28);
}

.btn[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Messages */
.error {
  margin: 4px 0 0;
  color: #ffd0d0;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0,0,0,0.35);
}

.success {
  margin: 4px 0 0;
  color: #caffd4;
  font-weight: 800;
  text-shadow: 0 1px 2px rgba(0,0,0,0.35);
}
</style>
